/**
 * FirmMoneyBarCard — firm-wide financial health at a glance.
 * Shows aggregated MoneyBar + collapsible per-fee-earner breakdown.
 * For: May (Accounts), Liezel/Vanessa (Directors).
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config/api";
import MoneyBar from "./MoneyBar";

export default function FirmMoneyBarCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/money-on-account/firm-summary`)
      .then(r => setData(r.data))
      .catch((err) => {
        // BM-H153 (finance audit P1-6): the firm-wide MoA bar sits on
        // director dashboards, so silent .catch(console.error) had it
        // rendering nothing without explanation when the endpoint
        // failed. Surface the error so it is clear the bar is missing
        // because the fetch failed, not because there is no data.
        console.error("Firm MoA summary failed:", err);
        if (window.errorMessage) {
          window.errorMessage(
            `Firm money-on-account summary failed to load: ${err?.response?.data?.detail || err?.message || "unknown error"}`
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse h-24 bg-navy-card rounded-lg" />;
  if (!data) return null;

  const { totals, by_fee_earner, active_matter_count } = data;

  // Hide entirely when no data to show
  const hasFinancials = totals.quote > 0 || totals.wip > 0 || totals.money_on_account > 0;
  if (!hasFinancials && active_matter_count === 0) return null;

  return (
    <div className="bg-navy-card border border-navy-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">
          Firm Financial Health
          <span className="ml-2 text-xs text-slate-400 font-normal">
            {active_matter_count} active matters
          </span>
        </h3>
        {by_fee_earner.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-teal hover:text-teal"
          >
            {expanded ? "Collapse" : "By fee earner \u25be"}
          </button>
        )}
      </div>

      {/* Firm-wide aggregated bar */}
      <MoneyBar
        quote={totals.quote}
        wip={totals.wip}
        moneyOnAccount={totals.money_on_account}
      />

      {/* Per fee earner breakdown */}
      {expanded && (
        <div className="mt-4 space-y-3 border-t border-navy-border pt-3">
          {by_fee_earner.map(fe => (
            <div key={fe.fee_earner}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-300 font-medium">
                  {fe.fee_earner}
                  <span className="text-slate-500 ml-1">({fe.matter_count})</span>
                </span>
              </div>
              <MoneyBar
                quote={fe.quote}
                wip={fe.wip}
                moneyOnAccount={fe.money_on_account}
                compact
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
